/**
 * RTWO Ledger & State Engine — Storage Abstraction Layer
 * Phase 5 | Version: 1.0 Storage Abstraction Edition
 *
 * Defines the storage contract for the Ledger & State Engine.
 * The Ledger Engine depends ONLY on these interfaces — never on
 * any concrete storage implementation.
 *
 * Future implementations may include:
 *   - RocksDB
 *   - LevelDB
 *   - PostgreSQL
 *   - Distributed Storage
 *   - Cloud Storage
 *
 * Current status: Not Configured
 * Source: Storage Abstraction Layer
 */

import type {
  RTWOLedgerState,
  RTWOStateTransition,
  RTWOAccountState,
  RTWOStorageNode,
  RTWOStateSnapshot,
  RTWOLedgerRecoveryPoint,
  LedgerHash,
  LedgerAddress,
  LedgerBlockHeight,
  LedgerSnapshotId,
  LedgerRecoveryPointId,
  LedgerTransitionId,
  StateTransitionStatus,
  SnapshotStatus,
  RecoveryPointStatus,
  AccountType,
  AccountStatus,
} from './domain-models';

// ─────────────────────────────────────────────
// STORAGE PROVIDER STATUS
// ─────────────────────────────────────────────

export type StorageProviderStatus =
  'not_configured' | 'connecting' | 'connected' | 'disconnected' | 'error';

export type StorageBackendType =
  'rocksdb' | 'leveldb' | 'postgresql' | 'distributed' | 'cloud' | 'in_memory' | 'not_configured';

export interface StorageProviderDescriptor {
  providerId: string;
  providerName: string;
  backendType: StorageBackendType;
  status: StorageProviderStatus;
  version: string;
  isConfigured: boolean;
  configuredAt: string | null;
  description: string;
}

// ─────────────────────────────────────────────
// STORAGE PROVIDER INTERFACE
// The single entry point for all storage operations.
// The Ledger Engine depends only on this interface.
// ─────────────────────────────────────────────

export interface StorageProvider {
  readonly providerId: string;
  readonly providerName: string;
  readonly backendType: StorageBackendType;

  /** Connect to the underlying storage backend. */
  connect(): Promise<void>;

  /** Disconnect from the underlying storage backend. */
  disconnect(): Promise<void>;

  /** Returns the current connection status. */
  getStatus(): StorageProviderStatus;

  /** Returns the full provider descriptor. */
  getDescriptor(): StorageProviderDescriptor;

  /** Returns true if the provider is ready for read/write operations. */
  isReady(): boolean;

  /** Expose typed repository accessors. */
  getLedgerRepository(): LedgerRepository;
  getStateRepository(): StateRepository;
  getSnapshotRepository(): SnapshotRepository;
  getRecoveryRepository(): RecoveryRepository;
  getStateStorageRepository(): StateStorageRepository;
}

// ─────────────────────────────────────────────
// REPOSITORY 1: LEDGER REPOSITORY
// Responsible for persisting and retrieving RTWOLedgerState.
// ─────────────────────────────────────────────

export interface LedgerRepository {
  /**
   * Persist the current ledger state.
   * Placeholder: requires real storage backend.
   */
  saveLedgerState(state: RTWOLedgerState): Promise<void>;

  /**
   * Retrieve the latest ledger state.
   * Returns null when storage is not configured.
   */
  getLatestLedgerState(): Promise<RTWOLedgerState | null>;

  /**
   * Retrieve ledger state at a specific block height.
   * Returns null when storage is not configured.
   */
  getLedgerStateAtBlock(blockHeight: LedgerBlockHeight): Promise<RTWOLedgerState | null>;

  /**
   * Delete ledger state records before a given block height.
   * Placeholder: pruning requires real storage.
   */
  pruneLedgerStatesBefore(blockHeight: LedgerBlockHeight): Promise<number>;
}

// ─────────────────────────────────────────────
// REPOSITORY 2: STATE REPOSITORY
// Responsible for persisting and retrieving RTWOStateTransition records.
// ─────────────────────────────────────────────

export interface StateRepository {
  /**
   * Persist a state transition record.
   * Placeholder: requires real storage backend.
   */
  saveTransition(transition: RTWOStateTransition): Promise<void>;

  /**
   * Retrieve a state transition by ID.
   * Returns null when storage is not configured.
   */
  getTransitionById(transitionId: LedgerTransitionId): Promise<RTWOStateTransition | null>;

  /**
   * Retrieve all transitions for a given block height.
   * Returns empty array when storage is not configured.
   */
  getTransitionsByBlock(blockHeight: LedgerBlockHeight): Promise<RTWOStateTransition[]>;

  /**
   * Update the status of a state transition.
   * Placeholder: requires real storage backend.
   */
  updateTransitionStatus(
    transitionId: LedgerTransitionId,
    status: StateTransitionStatus
  ): Promise<void>;

  /**
   * Delete transition records before a given block height.
   * Placeholder: pruning requires real storage.
   */
  pruneTransitionsBefore(blockHeight: LedgerBlockHeight): Promise<number>;
}

// ─────────────────────────────────────────────
// REPOSITORY 3: STATE STORAGE REPOSITORY
// Responsible for persisting and retrieving RTWOAccountState
// and RTWOStorageNode entries (Merkle Patricia Trie nodes).
// ─────────────────────────────────────────────

export interface StateStorageRepository {
  /**
   * Persist an account state entry.
   * Placeholder: requires real storage backend.
   */
  saveAccountState(account: RTWOAccountState): Promise<void>;

  /**
   * Retrieve account state by address.
   * Returns null when storage is not configured.
   */
  getAccountByAddress(address: LedgerAddress): Promise<RTWOAccountState | null>;

  /**
   * Retrieve all account state entries.
   * Returns empty array when storage is not configured.
   */
  getAllAccounts(): Promise<RTWOAccountState[]>;

  /**
   * Retrieve accounts filtered by type.
   * Returns empty array when storage is not configured.
   */
  getAccountsByType(accountType: AccountType): Promise<RTWOAccountState[]>;

  /**
   * Update the status of an account.
   * Placeholder: requires real storage backend.
   */
  updateAccountStatus(address: LedgerAddress, status: AccountStatus): Promise<void>;

  /**
   * Get the total count of stored accounts.
   * Returns null when storage is not configured.
   */
  getAccountCount(): Promise<number | null>;

  /**
   * Persist a storage tree node (MPT node).
   * Placeholder: requires real MPT storage backend.
   */
  saveStorageNode(node: RTWOStorageNode): Promise<void>;

  /**
   * Retrieve a storage tree node by hash.
   * Returns null when storage is not configured.
   */
  getStorageNodeByHash(nodeHash: LedgerHash): Promise<RTWOStorageNode | null>;

  /**
   * Retrieve child nodes of a given parent node.
   * Returns empty array when storage is not configured.
   */
  getChildNodes(parentHash: LedgerHash): Promise<RTWOStorageNode[]>;

  /**
   * Retrieve the root node of the storage tree.
   * Returns null when storage is not configured.
   */
  getRootNode(): Promise<RTWOStorageNode | null>;

  /**
   * Delete stale storage nodes before a given block height.
   * Placeholder: pruning requires real storage.
   */
  pruneNodesBefore(blockHeight: LedgerBlockHeight): Promise<number>;
}

// ─────────────────────────────────────────────
// REPOSITORY 4: SNAPSHOT REPOSITORY
// Responsible for persisting and retrieving RTWOStateSnapshot records.
// ─────────────────────────────────────────────

export interface SnapshotRepository {
  /**
   * Persist a state snapshot.
   * Placeholder: requires real storage backend.
   */
  saveSnapshot(snapshot: RTWOStateSnapshot): Promise<void>;

  /**
   * Retrieve a snapshot by ID.
   * Returns null when storage is not configured.
   */
  getSnapshotById(snapshotId: LedgerSnapshotId): Promise<RTWOStateSnapshot | null>;

  /**
   * Retrieve the latest snapshot.
   * Returns null when storage is not configured.
   */
  getLatestSnapshot(): Promise<RTWOStateSnapshot | null>;

  /**
   * Retrieve all snapshots for a given block height.
   * Returns empty array when storage is not configured.
   */
  getSnapshotsByBlock(blockHeight: LedgerBlockHeight): Promise<RTWOStateSnapshot[]>;

  /**
   * Update the status of a snapshot.
   * Placeholder: requires real storage backend.
   */
  updateSnapshotStatus(snapshotId: LedgerSnapshotId, status: SnapshotStatus): Promise<void>;

  /**
   * Delete archived snapshots before a given block height.
   * Placeholder: pruning requires real storage.
   */
  pruneSnapshotsBefore(blockHeight: LedgerBlockHeight): Promise<number>;
}

// ─────────────────────────────────────────────
// REPOSITORY 5: RECOVERY REPOSITORY
// Responsible for persisting and retrieving RTWOLedgerRecoveryPoint records.
// ─────────────────────────────────────────────

export interface RecoveryRepository {
  /**
   * Persist a recovery point.
   * Placeholder: requires real storage backend.
   */
  saveRecoveryPoint(recoveryPoint: RTWOLedgerRecoveryPoint): Promise<void>;

  /**
   * Retrieve a recovery point by ID.
   * Returns null when storage is not configured.
   */
  getRecoveryPointById(
    recoveryPointId: LedgerRecoveryPointId
  ): Promise<RTWOLedgerRecoveryPoint | null>;

  /**
   * Retrieve the latest recovery point.
   * Returns null when storage is not configured.
   */
  getLatestRecoveryPoint(): Promise<RTWOLedgerRecoveryPoint | null>;

  /**
   * Retrieve all recovery points for a given block height.
   * Returns empty array when storage is not configured.
   */
  getRecoveryPointsByBlock(blockHeight: LedgerBlockHeight): Promise<RTWOLedgerRecoveryPoint[]>;

  /**
   * Update the status of a recovery point.
   * Placeholder: requires real storage backend.
   */
  updateRecoveryPointStatus(
    recoveryPointId: LedgerRecoveryPointId,
    status: RecoveryPointStatus
  ): Promise<void>;

  /**
   * Delete recovery points before a given block height.
   * Placeholder: pruning requires real storage.
   */
  pruneRecoveryPointsBefore(blockHeight: LedgerBlockHeight): Promise<number>;
}

// ─────────────────────────────────────────────
// NULL STORAGE PROVIDER
// Default implementation when no backend is configured.
// Returns null/empty for all operations — never throws.
// ─────────────────────────────────────────────

const NULL_LEDGER_REPO: LedgerRepository = {
  saveLedgerState: async () => {},
  getLatestLedgerState: async () => null,
  getLedgerStateAtBlock: async () => null,
  pruneLedgerStatesBefore: async () => 0,
};

const NULL_STATE_REPO: StateRepository = {
  saveTransition: async () => {},
  getTransitionById: async () => null,
  getTransitionsByBlock: async () => [],
  updateTransitionStatus: async () => {},
  pruneTransitionsBefore: async () => 0,
};

const NULL_STATE_STORAGE_REPO: StateStorageRepository = {
  saveAccountState: async () => {},
  getAccountByAddress: async () => null,
  getAllAccounts: async () => [],
  getAccountsByType: async () => [],
  updateAccountStatus: async () => {},
  getAccountCount: async () => null,
  saveStorageNode: async () => {},
  getStorageNodeByHash: async () => null,
  getChildNodes: async () => [],
  getRootNode: async () => null,
  pruneNodesBefore: async () => 0,
};

const NULL_SNAPSHOT_REPO: SnapshotRepository = {
  saveSnapshot: async () => {},
  getSnapshotById: async () => null,
  getLatestSnapshot: async () => null,
  getSnapshotsByBlock: async () => [],
  updateSnapshotStatus: async () => {},
  pruneSnapshotsBefore: async () => 0,
};

const NULL_RECOVERY_REPO: RecoveryRepository = {
  saveRecoveryPoint: async () => {},
  getRecoveryPointById: async () => null,
  getLatestRecoveryPoint: async () => null,
  getRecoveryPointsByBlock: async () => [],
  updateRecoveryPointStatus: async () => {},
  pruneRecoveryPointsBefore: async () => 0,
};

/**
 * NullStorageProvider — the default StorageProvider when no backend is configured.
 * All repository methods return null/empty. Status is always 'not_configured'.
 * The Ledger Engine uses this provider until a real backend is registered.
 */
export class NullStorageProvider implements StorageProvider {
  readonly providerId = 'null-storage-provider';
  readonly providerName = 'Null Storage Provider';
  readonly backendType: StorageBackendType = 'not_configured';

  connect = async (): Promise<void> => {};
  disconnect = async (): Promise<void> => {};
  getStatus = (): StorageProviderStatus => 'not_configured';
  isReady = (): boolean => false;

  getDescriptor(): StorageProviderDescriptor {
    return {
      providerId: this.providerId,
      providerName: this.providerName,
      backendType: this.backendType,
      status: 'not_configured',
      version: '1.0.0',
      isConfigured: false,
      configuredAt: null,
      description:
        'Default null provider. No storage backend is configured. ' +
        'Register a real StorageProvider implementation to enable persistence.',
    };
  }

  getLedgerRepository = (): LedgerRepository => NULL_LEDGER_REPO;
  getStateRepository = (): StateRepository => NULL_STATE_REPO;
  getSnapshotRepository = (): SnapshotRepository => NULL_SNAPSHOT_REPO;
  getRecoveryRepository = (): RecoveryRepository => NULL_RECOVERY_REPO;
  getStateStorageRepository = (): StateStorageRepository => NULL_STATE_STORAGE_REPO;
}

// ─────────────────────────────────────────────
// STORAGE PROVIDER REGISTRY
// Singleton registry — the Ledger Engine reads the active provider here.
// ─────────────────────────────────────────────

let _activeProvider: StorageProvider = new NullStorageProvider();

export const StorageProviderRegistry = {
  /**
   * Register a real storage provider implementation.
   * Call this once during application bootstrap when a backend is available.
   */
  register(provider: StorageProvider): void {
    _activeProvider = provider;
  },

  /**
   * Get the currently active storage provider.
   * Returns NullStorageProvider when no backend is configured.
   */
  getProvider(): StorageProvider {
    return _activeProvider;
  },

  /**
   * Returns true if a real (non-null) storage backend is configured.
   */
  isConfigured(): boolean {
    return _activeProvider.backendType !== 'not_configured';
  },

  /**
   * Returns the current provider status.
   */
  getStatus(): StorageProviderStatus {
    return _activeProvider.getStatus();
  },
};
